/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * TK-26 (10/10/2026) — HAI CHIỀU ĐỒNG BỘ LỊCH, NÓI TRƯỚC "BẤM THÌ ĐỔI GÌ".
 *
 * lee: *"cho tôi 2 phương án: 1 là đồng bộ tủ về server, 2 là server về tủ. Lúc đồng bộ báo tôi thao tác đó thay
 * đổi gì, cần diễn tả ngắn gọn"*.
 *
 * Nguồn duy nhất giao diện có TRƯỚC khi bấm là câu của hồ sơ SCHEDULE_MISMATCH — máy chủ dựng bằng
 * `diffSchedules()` (server/src/services/scheduleMap.ts), mỗi dòng một kiểu:
 *   "Ngõ ra 2: server có 0 timer, tủ báo 2"                       → SỐ giờ bật khác
 *   "Ngõ ra 1 timer 0: server 18:00–23:00 ≠ tủ 04:00–05:30"        → GIỜ khác (cùng số)
 *   "Ngõ ra 3: cho phép true ≠ tủ báo false"                       → ngõ bật/tắt khác
 * Hàm này đọc ba kiểu ấy và kể lại THEO CHIỀU người bấm chọn. Dòng không đọc được ⇒ in nguyên văn (đã đổi
 * "server" → "máy chủ"), không giấu. Máy chủ chỉ ghi 5 dòng đầu ⇒ đủ 5 dòng thì nói "có thể còn khác".
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
export type ChieuDongBo = 'tu-ve-may-chu' | 'may-chu-xuong-tu';

interface NgoLech { so?: { may: number; tu: number }; gio: { may: string; tu: string }[]; choPhep?: { may: boolean; tu: boolean }; tho: string[] }

export function docLechLich(message: string | null | undefined): Map<number, NgoLech> {
  const ra = new Map<number, NgoLech>();
  const lay = (n: number) => { const v = ra.get(n) ?? { gio: [], tho: [] }; ra.set(n, v); return v; };
  for (const dong of (message ?? '').split('\n').slice(1).map((d) => d.trim()).filter(Boolean)) {
    let m = /^Ng[õo] ra (\d+): server có (\d+) timer, tủ báo (\d+)$/.exec(dong);
    if (m) { lay(+m[1]).so = { may: +m[2], tu: +m[3] }; continue; }
    m = /^Ng[õo] ra (\d+) timer \d+: server (\S+–\S+) ≠ tủ (\S+–\S+)$/.exec(dong);
    if (m) { lay(+m[1]).gio.push({ may: m[2], tu: m[3] }); continue; }
    m = /^Ng[õo] ra (\d+): cho phép (true|false) ≠ tủ báo (true|false)$/.exec(dong);
    if (m) { lay(+m[1]).choPhep = { may: m[2] === 'true', tu: m[3] === 'true' }; continue; }
    const n = /^Ng[õo] ra (\d+)/.exec(dong);
    lay(n ? +n[1] : 0).tho.push(dong.replace(/\bserver\b/gi, 'máy chủ'));
  }
  return ra;
}

/**
 * Câu ngắn theo chiều:
 *   tủ → máy chủ: "Ngõ 2: bản máy chủ 0 → 2 giờ bật"           (đèn KHÔNG đổi — tủ giữ nguyên)
 *   máy chủ → tủ: "Ngõ 2: tủ 2 → 0 giờ bật — ngõ này sẽ không còn giờ bật nào"   (đèn ĐỔI theo)
 */
export function cauDongBo(message: string | null | undefined, chieu: ChieuDongBo): string[] {
  const lech = docLechLich(message);
  const ben = chieu === 'tu-ve-may-chu' ? 'bản máy chủ' : 'tủ';
  const ra: string[] = [];
  for (const [ngo, l] of [...lech.entries()].sort((a, b) => a[0] - b[0])) {
    const nhan = ngo > 0 ? `Ngõ ${ngo}` : 'Khác';
    const phan: string[] = [];
    if (l.so) {
      const [tu, den] = chieu === 'tu-ve-may-chu' ? [l.so.may, l.so.tu] : [l.so.tu, l.so.may];
      phan.push(`${ben} ${tu} → ${den} giờ bật${den === 0 ? ' — ngõ này sẽ không còn giờ bật nào' : ''}`);
    }
    if (l.gio.length) {
      const doi = l.gio.slice(0, 2).map((g) => (chieu === 'tu-ve-may-chu' ? `${g.may} → ${g.tu}` : `${g.tu} → ${g.may}`));
      phan.push(`${ben} đổi giờ ${doi.join(', ')}${l.gio.length > 2 ? ', …' : ''}`);
    }
    if (l.choPhep) {
      const den = chieu === 'tu-ve-may-chu' ? l.choPhep.tu : l.choPhep.may;
      phan.push(`${ben} ${den ? 'BẬT' : 'TẮT'} ngõ này`);
    }
    if (phan.length) ra.push(`${nhan}: ${phan.join(' · ')}`);
    for (const t of l.tho) ra.push(t);
  }
  const soDong = (message ?? '').split('\n').slice(1).filter((d) => d.trim()).length;
  if (soDong >= 5) ra.push('(máy chủ chỉ ghi 5 chỗ lệch đầu — có thể còn chỗ khác)');
  return ra;
}

/** Câu kết của từng chiều: cái gì ĐỔI, cái gì GIỮ — ngắn, một dòng. */
export const CAU_KET_DONG_BO: Record<ChieuDongBo, string> = {
  'tu-ve-may-chu': 'Tủ giữ nguyên, đèn không đổi. Hồ sơ tự đóng ở lần tủ báo lịch kế tiếp.',
  'may-chu-xuong-tu': 'Tủ chạy theo lịch mới ngay khi nhận. Hồ sơ tự đóng khi tủ báo lịch khớp.',
};
